import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'rc-slider';
import styles from './ZoomVideoControls.module.scss';
import TimeInput from '../../../../components/TimeInput/TimeInput';
import { Icon } from '../../../../components/Icon';
import controlBgImage from './controlBgImage.svg';
import position from './position.svg';
import Waveform from '../../../../components/Waveform/Waveform';
import FloatingContainer from '../../../../routes/newVideo/VideoTrimmingControls/FloatingContainer';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../../common/components/Spinner';
import cx from 'classnames';
import { useZoomContext } from '../../../../context/ZoomContext';
import usePrevious from '../../../../../common/hooks/usePrevious';

const MIN_ZOOM_DURATION = 1000; // value in ms

const MIN_HEIGHT_COEF = 0.75;
const NORMAN_HEIGHT = 56;
const HANDLER_NORMAN_HEIGHT = 65;
const RAIL_NORMAN_HEIGHT = 58;

const POSITION_TYPES = {
  start: 0,
  end: 1,
};

const ZoomVideoControls = ({ activeScene, videoDuration, waveform = null, className, min }) => {
  const {
    zooms,
    zoomProgress,
    updateZooms,
    updateInitialZooms,
    selectedZoom,
    changeZooms,
    updateZoomsSeekValue,
    updateZoomProgress,
    changeSelectedZoom,
    zoomChanged,
    removeZoom,
  } = useZoomContext();

  // zoomPoint with the left or right slider selected
  const [typePosition, setTypePosition] = useState(POSITION_TYPES.start);

  const [showSeekTo, setShowSeekTo] = useState(false); // to show current cursor position
  const [pointerPosition, setPointerPosition] = useState(0); // to show current cursor position
  const [isRangeChanging, setIsRangeChanging] = useState(false); // to hide opacity cursor when changing

  // Show start or end time
  const [handlersPositions, setHandlersPositions] = useState([0, 1]);

  const [isDragZoomPointActive, setIsDragZoomPointActive] = useState(false);
  const [startDragPosition, setStartDragPosition] = useState(0); // [0, 1]

  const [orderOfChange, setOrderOfChange] = useState(0);

  const [initialDragOffset, setInitialDragOffset] = useState(0);
  const [isDragInvalid, setIsDragInvalid] = useState(false);

  const [temporarySeekValue, setTemporarySeekValue] = useState(null);
  const [isDragged, setIsDragged] = useState(false);

  const currentStartTime = useMemo(() => zooms.find(zoom => zoom.id === selectedZoom.id)?.offset || 0, [zooms, selectedZoom.id]);

  const currentEndTime = useMemo(
    () =>
      (zooms.find(zoom => zoom.id === selectedZoom.id)?.offset || 0) +
      (zooms.find(zoom => zoom.id === selectedZoom.id)?.duration || 1000),
    [zooms, selectedZoom.id]
  );

  // We check which zooms are completely included in the interval of the cropped video.
  // We create a separate variable, instead of directly writing to the zoom context,
  // because we should save the old values also for the case
  // if the user returns the old trim value and all previous zooms are saved
  const zoomsWithTrimming = useMemo(
    () =>
      zooms.filter(({ id }) => {
        const { offset, duration } = activeScene?.zoomPoints.find(zoomPoint => zoomPoint?.id === id) || {};

        // The duration value does not exist for newly added scales in zoomPoints in the active scene
        // because they were not added there yet before saving
        // So the new zoom is already within the trimming range
        if (!duration) {
          return true;
        }

        const isZoomEndBeforeTrimStart = (offset + duration) <= activeScene?.trimStart;
        const isZoomStartAfterTrimEnd = offset >= activeScene?.trimEnd;

        return !(isZoomEndBeforeTrimStart || isZoomStartAfterTrimEnd);
      }),
    [activeScene, videoDuration, zooms]
  );

  const previousSceneId = usePrevious(activeScene?.id);

  const { t } = useTranslation();

  const callZoomRef = useRef(null);

  const handleSelectZoom = zoomId => {
    const currentZoom = zooms.find(({ id }) => id === zoomId) || {};
    changeSelectedZoom(currentZoom);
  };

  const updateZoomTimestamp = (id, startPosition, endPosition) => {
    const offset = startPosition * videoDuration;
    const duration = endPosition * videoDuration - offset;

    if (duration < MIN_ZOOM_DURATION / (activeScene?.playbackRate || 1)) {
      return;
    }

    zoomChanged(true);

    const sortedByStartTime = zooms.sort((a, b) => a.offset - b.offset);
    const currentIndex = sortedByStartTime.map(({ id }) => id).indexOf(id);

    const leftClosest = sortedByStartTime[currentIndex - 1];
    const rightClosest = sortedByStartTime[currentIndex + 1];

    const leftCheck = leftClosest && leftClosest.offset + leftClosest.duration >= offset;
    const rightCheck = rightClosest && offset + duration >= rightClosest.offset;

    if (leftCheck) {
      const newOffset = leftClosest.offset + leftClosest.duration;
      changeZooms(id, {
        offset: Math.round(newOffset),
        duration: Math.round(endPosition * videoDuration - newOffset),
        order: orderOfChange,
      });

      setOrderOfChange(prevState => prevState + 1);
      if (!rightCheck) return;
    }

    if (rightClosest && offset + duration >= rightClosest.offset) {
      changeZooms(id, { offset: Math.round(offset), duration: Math.round(rightClosest.offset - offset - 1), order: orderOfChange });

      setOrderOfChange(prevState => prevState + 1);
      return;
    }

    changeZooms(id, { offset: Math.round(offset), duration: Math.round(duration), order: orderOfChange });
    setOrderOfChange(prevState => prevState + 1);
    setIsRangeChanging(true);
  };

  const onSeekChange = useCallback(
    rangeValue => {
      const currentTime = rangeValue * videoDuration;
      const currentZoom = zoomsWithTrimming.find(({ offset, duration }) => offset <= currentTime && currentTime <= offset + duration);

      /**
       * After adding the drag functionality directly to the zoom range element,
       * we need to determine whether it was just a click and release of the mouse without moving it or not
       * If there was a normal mouse click, set a new ZoomsSeekValue.
       * If there was a zoom movement, we ignore setting the value for ZoomsSeekValue.
       * This is done to separate these two actions.
       */
      if (currentZoom && !callZoomRef?.current?.isHandler) {
        changeSelectedZoom(currentZoom);
        // This value will be set to updateZoomsSeekValue() after releasing the mouse button if there was no zoom movement
        setTemporarySeekValue(rangeValue);
        startDragActive({ rangeValue, zoomToMove: currentZoom });
      } else {
        updateZoomsSeekValue(rangeValue);
      }
    },
    [updateZoomsSeekValue, zoomsWithTrimming, videoDuration, selectedZoom]
  );

  const handleOnMouseMove = e => {
    const cursorPosition = e.clientX - callZoomRef.current.offsetLeft - callZoomRef.current.offsetParent.offsetLeft;
    setPointerPosition(cursorPosition - 6);
  };

  const handleMouseDown = e => {
    callZoomRef.current.isHandler = Array.from(e?.target?.classList).indexOf('rc-slider-handle') >= 0;

    const tabIndex = e.target.tabIndex;

    if (tabIndex >= 0) {
      setTypePosition(tabIndex);
    }
  };

  const startDragActive = ({ event, rangeValue, zoomToMove }) => {
    setIsDragZoomPointActive(true);

    /**
     * Zoom movements are available in two ways:
     * 1) Via an icon outside the slider
     * In this case, we use the current position of the mouse
     * 2) By clicking on the zoom slider itself directly
     * In this case, we use the current range value
     * */
    if (event) {
      setStartDragPosition(event.clientX - callZoomRef.current.offsetLeft - callZoomRef.current.offsetParent.offsetLeft);
      setIsDragged(true);
    }

    if (rangeValue) {
      setStartDragPosition(callZoomRef.current.offsetWidth * rangeValue);
    }

    const currentZoom = zooms.find(({ id }) => id === zoomToMove.id) || {};

    setInitialDragOffset(currentZoom.offset);
  };

  const stopDragActive = () => {
    setIsDragZoomPointActive(false);
    setStartDragPosition(0);

    if (isDragInvalid) {
      changeZooms(selectedZoom.id, { offset: initialDragOffset });
    } else {
      changeZooms(selectedZoom.id, { order: orderOfChange });
      setOrderOfChange(prevState => prevState + 1);

      zoomChanged(true);
    }

    if (!isDragged || callZoomRef?.current?.isHandler) {
      //There was no zoom movement - set temporarySeekValue
      updateZoomsSeekValue(temporarySeekValue);
    }

    setIsDragged(false);
    setIsDragInvalid(false);
  };

  const zoomDragListener = event => {
    setIsDragged(true);

    const currentDragPosition = event.clientX - callZoomRef.current.offsetLeft - callZoomRef.current.offsetParent.offsetLeft;

    const minAvailableValue = 0;
    const maxAvailableValue = callZoomRef.current.offsetWidth;

    const rangeInPercents = (currentDragPosition - startDragPosition) / maxAvailableValue;

    const { offset, duration } = zooms.find(zoom => zoom.id === selectedZoom.id) || {};

    let newOffset = offset + videoDuration * rangeInPercents;

    if (currentDragPosition <= minAvailableValue) {
      newOffset = 0;
    }

    if (currentDragPosition >= maxAvailableValue) {
      newOffset = videoDuration - duration;
    }

    if (newOffset < 0 || newOffset + duration > videoDuration) {
      return;
    }

    const zoomsRange = zooms
      .filter(zoom => zoom.id !== selectedZoom.id)
      .map(({ offset, duration }) => ({ startTime: offset, endTime: offset + duration }));

    const isInvalidTimestamps = zoomsRange.some(({ startTime, endTime }) => {
      const isStartInside = startTime <= newOffset && newOffset <= endTime;
      const isEndInside = startTime <= newOffset + duration && newOffset + duration <= endTime;

      const isInsideZoom = newOffset <= startTime && endTime <= newOffset + duration;

      return isStartInside || isEndInside || isInsideZoom;
    });

    changeZooms(selectedZoom.id, { offset: Math.round(newOffset) });

    setIsDragInvalid(isInvalidTimestamps);
  };

  const handleDeleteZoom = () => {
    removeZoom(selectedZoom.id);
    zoomChanged(true);
  };

  const trackLeftRightBorder = useCallback(
    zoomId => {
      if (isDragged && selectedZoom.id === zoomId) {
        return isDragInvalid ? '1px solid red' : '1px solid #7C43AA';
      }

      if (selectedZoom.id !== zoomId) {
        return '1px solid #7C43AA';
      }

      return 'none';
    },
    [selectedZoom, isDragInvalid, isDragged]
  );

  useEffect(() => {
    setHandlersPositions([currentStartTime / videoDuration, currentEndTime / videoDuration]);
  }, [zooms, currentStartTime, currentEndTime, videoDuration, selectedZoom.id]);

  useEffect(() => {
    if (isDragZoomPointActive) {
      document.addEventListener('mouseup', stopDragActive);
    }

    return async () => {
      document.removeEventListener('mouseup', stopDragActive);
    };
  }, [isDragInvalid, initialDragOffset, isDragZoomPointActive, isDragged]);

  useEffect(() => {
    if (isDragZoomPointActive) {
      document.addEventListener('mousemove', zoomDragListener);
    }

    return () => document.removeEventListener('mousemove', zoomDragListener);
  }, [isDragZoomPointActive]);

  useEffect(() => {
    const body = document.querySelector('body');
    body.addEventListener('mousedown', handleMouseDown);

    if (zoomsWithTrimming[0]) {
      changeSelectedZoom(zoomsWithTrimming[0]);
    } else {
      changeSelectedZoom({});
    }

    updateZoomsSeekValue(zoomProgress.percents);

    return () => {
      body.removeEventListener('mousedown', handleMouseDown);
      changeSelectedZoom({});
    };
  }, []);

  useEffect(() => {
    if (previousSceneId && activeScene?.id && previousSceneId !== activeScene?.id) {
      updateZoomsSeekValue(0);
      updateZoomProgress({ percents: 0, milliseconds: activeScene?.trimStart });
    }
  }, [previousSceneId, activeScene]);

  useEffect(() => {
    if (previousSceneId === activeScene?.id) {
      return;
    }

    if (activeScene?.zoomPoints?.length) {
      const { zoomPoints } = activeScene;
      // The backend stores the real offset and duration values, so that if the start of the video or playback speed changes,
      // the zoom remains in the correct position
      // Therefore, we need to adjust these values to the current scene settings
      const mutatedZoomPoints = zoomPoints
        .sort((a, b) => a.offset - b.offset)
        .map(zoomPoint => {
          const mutatedOffset = (zoomPoint.offset - activeScene?.trimStart) / (activeScene?.playbackRate || 1);
          const mutatedDuration = zoomPoint.duration / (activeScene?.playbackRate || 1);

          const isZoomStartBeforeTrimStart = zoomPoint.offset < activeScene?.trimStart;
          const isZoomEndAfterTrimEnd = zoomPoint.offset - activeScene?.trimStart + zoomPoint.duration > activeScene?.trimmedDuration;

          if (isZoomStartBeforeTrimStart) {
            return {
              ...zoomPoint,
              offset: 0,
              duration: mutatedDuration - activeScene?.trimStart / (activeScene?.playbackRate || 1),
            };
          }

          if (isZoomEndAfterTrimEnd) {
            return {
              ...zoomPoint,
              offset: mutatedOffset,
              duration: activeScene?.trimmedDuration / (activeScene?.playbackRate || 1),
            };
          }

          return {
            ...zoomPoint,
            offset: mutatedOffset,
            duration: mutatedDuration,
          };
        });

      updateZooms(mutatedZoomPoints);
      updateInitialZooms(mutatedZoomPoints);

      const { id: zoomIdToSelect } =
        zoomPoints.find(({ offset, duration }) => {
          const isZoomEndBeforeTrimStart = (offset + duration) <= activeScene?.trimStart;
          const isZoomStartAfterTrimEnd = offset >= activeScene?.trimEnd;
          return !(isZoomEndBeforeTrimStart || isZoomStartAfterTrimEnd)
        }) || {};

      const zoomToSelect = mutatedZoomPoints.find(mutatedZoomPoint => mutatedZoomPoint?.id === zoomIdToSelect);

      if (zoomToSelect) {
        changeSelectedZoom(zoomToSelect);
      } else {
        changeSelectedZoom({});
      }
    } else {
      updateZooms([]);
      updateInitialZooms([]);
      changeSelectedZoom({});
    }
  }, [activeScene]);

  return (
    <div className={cx(styles.zoomsInstruments, className)} ref={callZoomRef}>
      {showSeekTo && !isRangeChanging && !isDragged && (
        <div className={cx(styles.cursor, { [styles.min]: min })} style={{ left: `${pointerPosition}px` }}></div>
      )}
      <div
        className={cx(styles.slidersWrapper, { [styles.min]: min })}
        onMouseMove={handleOnMouseMove}
        onMouseOver={() => setShowSeekTo(true)}
        onMouseLeave={() => setShowSeekTo(false)}
      >
        {typePosition >= 0 && !!selectedZoom.id && (
          <FloatingContainer
            className={styles.dragIconContainer}
            parentRef={callZoomRef}
            floatingValue={handlersPositions[typePosition]}
            correction={typePosition ? -7 : -15}
          >
            <Icon
              src="dragIcon"
              className={cx(styles.dragIcon, {
                [styles.startPosition]: typePosition === POSITION_TYPES.start,
                [styles.endPosition]: typePosition === POSITION_TYPES.end,
              })}
            />
          </FloatingContainer>
        )}

        {!!selectedZoom.id && (
          <FloatingContainer
            className={cx(styles.dragAction, { [styles.min]: min })}
            parentRef={callZoomRef}
            floatingValue={
              handlersPositions[POSITION_TYPES.end] -
              (handlersPositions[POSITION_TYPES.end] - handlersPositions[POSITION_TYPES.start]) / 2
            }
            tooltip={t('my_video.tools.zoom.tooltip.drag')}
            disabled={isDragged}
            customTooltipStyles={{ left: '50%', transform: 'translateX(-50%)', zIndex: 56 }}
            correction={8}
            minLeftOffset={225}
          >
            <Icon
              src="dragAction"
              className={styles.dragIcon}
              onMouseDown={event => startDragActive({ event, zoomToMove: selectedZoom })}
            />
          </FloatingContainer>
        )}

        {!!selectedZoom.id && (
          <FloatingContainer
            className={cx(styles.binAction, { [styles.min]: min })}
            parentRef={callZoomRef}
            floatingValue={
              handlersPositions[POSITION_TYPES.end] -
              (handlersPositions[POSITION_TYPES.end] - handlersPositions[POSITION_TYPES.start]) / 2
            }
            tooltip={t('my_video.tools.zoom.tooltip.delete')}
            customTooltipStyles={{ left: '50%', transform: 'translateX(-50%)', zIndex: 56 }}
            correction={8}
            minLeftOffset={225}
          >
            <Icon src="bin" className={styles.dragIcon} onMouseDown={handleDeleteZoom} />
          </FloatingContainer>
        )}

        {zoomsWithTrimming.length > 0 ? (
          zoomsWithTrimming.map((zoom, index) => (
            <Slider
              key={zoom?.id}
              range
              defaultValue={[0, 1]}
              value={[zoom.offset / videoDuration, (zoom.offset + zoom.duration) / videoDuration]}
              min={0}
              max={1}
              tabIndex={[0, 1]}
              step={1 / (videoDuration / 100)}
              allowCross={false}
              onChange={([startTime, endTime]) => updateZoomTimestamp(zoom.id, startTime, endTime)}
              className={cx(styles.range, { [styles.draggableRange]: isDragged })}
              trackStyle={[
                {
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
                  border: 'none',
                  borderTop: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  borderBottom: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  borderLeft: trackLeftRightBorder(zoom.id),
                  borderRight: trackLeftRightBorder(zoom.id),
                  zIndex: selectedZoom.id === zoom.id && (isDragInvalid || isDragged) ? 52 : 3,
                  ...(selectedZoom.id === zoom.id && !isDragged && { borderRadius: '0' }),
                },
              ]}
              handleStyle={[
                {
                  display: selectedZoom.id === zoom.id && !isDragged ? 'inline-block' : 'none',
                  width: '12px',
                  height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
                  backgroundColor: selectedZoom.id === zoom.id && typePosition === POSITION_TYPES.start ? '#7C43AA' : '#BDBDBD',
                  backgroundImage: `url(${controlBgImage})`,
                  backgroundPosition: '50% 50%',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '0',
                  borderTopLeftRadius: '4px',
                  borderBottomLeftRadius: '4px',
                  border: 'none',
                  borderTop: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  borderLeft: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  borderBottom: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  boxShadow: 'none',
                  outline: 'none',
                  position: 'absolute',
                  marginLeft: '-12px', // 0px
                  zIndex: '51',
                  top: '10px',
                },
                {
                  display: selectedZoom.id === zoom.id && !isDragged ? 'inline-block' : 'none',
                  width: '12px',
                  height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
                  backgroundColor: selectedZoom.id === zoom.id && typePosition === POSITION_TYPES.end ? '#7C43AA' : '#BDBDBD',
                  backgroundImage: `url(${controlBgImage})`,
                  backgroundPosition: '50% 50%',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '0',
                  borderTopRightRadius: '4px',
                  borderBottomRightRadius: '4px',
                  border: 'none',
                  borderTop: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  borderRight: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  borderBottom: selectedZoom.id === zoom.id && isDragInvalid ? '1px solid red' : '1px solid #7C43AA',
                  boxShadow: 'none',
                  outline: 'none',
                  position: 'absolute',
                  top: '10px',
                  right: '0',
                  marginLeft: '0', // -12px
                  zIndex: '51',
                },
              ]}
              railStyle={{
                height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
                border: '1px solid #BDBDBD',
                background: index === 0 ? '#EEEEEE' : 'none',
                borderRadius: '4px',
                boxShadow: 'none',
              }}
              onBeforeChange={() => handleSelectZoom(zoom.id)}
              onAfterChange={() => setIsRangeChanging(false)}
            />
          ))
        ) : (
          <Slider
            range
            defaultValue={[0, 1]}
            min={0}
            max={1}
            tabIndex={[0, 1]}
            step={1 / (videoDuration / 100)}
            allowCross={false}
            className={styles.range}
            trackStyle={[
              {
                display: 'none',
              },
            ]}
            handleStyle={[
              {
                display: 'none',
              },
              {
                display: 'none',
              },
            ]}
            railStyle={{
              height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
              border: '1px solid #BDBDBD',
              background: '#EEEEEE',
              borderRadius: '4px',
              boxShadow: 'none',
            }}
          />
        )}

        <Slider
          className={styles.videoProgress}
          trackStyle={{
            backgroundColor: 'rgba(124, 67, 170, 0.1)',
            height: `${NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            top: '30%',
            border: '0px',
            zIndex: 3,
          }}
          handleStyle={{
            backgroundImage: `url(${position})`,
            backgroundPosition: '50% 0%',
            borderRadius: '0',
            border: 'none',
            boxShadow: 'none',
            outline: 'none',
            cursor: 'default',
            backgroundRepeat: 'no-repeat',
            backgroundColor: 'transparent',
            zIndex: '50',
            height: `${HANDLER_NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            top: '2px',
          }}
          railStyle={{
            height: `${RAIL_NORMAN_HEIGHT * (min ? MIN_HEIGHT_COEF : 1)}px`,
            background: 'transparent',
            zIndex: '50',
            boxShadow: 'none',
          }}
          min={0}
          max={1}
          step={0.001}
          onChange={onSeekChange}
          value={zoomProgress.percents}
          included
        />

        {waveform ? (
          <div className={cx(styles.waveformContainer, { [styles.min]: min })}>
            <Waveform
              waveformData={waveform.map(item => item * 10000)}
              parentRef={callZoomRef}
              trimStartPos={0}
              progress={zoomProgress.percents}
              min={min}
            />
          </div>
        ) : (
          <Spinner width="30px" height="30px" size="3px" className={styles.spinnerIcon} />
        )}
        <FloatingContainer
          parentRef={callZoomRef}
          floatingValue={zoomProgress.percents}
          correction={8}
          className={cx(styles.progressContainer, { [styles.min]: min })}
        >
          <TimeInput
            inputClassName={cx(styles.timeInput, { [styles.min]: min })}
            disabled={true}
            value={(zoomProgress.percents * videoDuration) / 1000}
            variant="basic"
            showControls={false}
          />
        </FloatingContainer>
        {typePosition >= 0 && !!selectedZoom.id && (
          <FloatingContainer className={styles.zoomsContainer} parentRef={callZoomRef} floatingValue={handlersPositions[typePosition]}>
            {typePosition === 0 ? (
              <TimeInput
                inputClassName={cx(styles.timeInput, { [styles.min]: min })}
                disabled={true}
                value={currentStartTime / 1000}
                variant="basic"
                showControls={false}
              />
            ) : (
              <TimeInput
                className={cx(styles.timeInput, { [styles.min]: min })}
                disabled={true}
                value={currentEndTime / 1000}
                variant="basic"
                showControls={false}
              />
            )}
            <div className={styles.keyHint}>
              <span>{t('my_video.tools.trim.use')}</span>
              <Icon src="keyArrowLeft" className={styles.keyIcon} />
              <Icon src="keyArrowRight" className={styles.keyIcon} />
            </div>
          </FloatingContainer>
        )}
      </div>
    </div>
  );
};

export default ZoomVideoControls;
